import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ProjectCard } from '@/components/projects/ProjectCard';
import ProceduralGroundBackground from '@/components/ui/procedural-ground-background';
import { useProjects } from '@/hooks/use-projects';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, Code2, Palette, Layers, ArrowRight, Sparkles, Zap } from 'lucide-react';

type ProjectType = 'all' | 'design' | 'dev' | 'integrated';

const projectTypes = [
  { id: 'all' as ProjectType, label: 'Todos', icon: Layers },
  { id: 'design' as ProjectType, label: 'Design', icon: Palette },
  { id: 'dev' as ProjectType, label: 'Desenvolvimento', icon: Code2 },
  { id: 'integrated' as ProjectType, label: 'Integrados', icon: Layers },
];

export default function Projetos() {
  const { data: projects, isLoading, error } = useProjects();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDiscipline, setSelectedDiscipline] = useState<string>('');
  const [selectedType, setSelectedType] = useState<ProjectType>('all');

  const allProjects = projects?.map((project) => {
    // Infer project type based on disciplines
    const disciplines = project.disciplines?.map((d: any) => typeof d === 'string' ? d : d.discipline) || [];
    const isDev = disciplines.some(d =>
      ['Desenvolvimento', 'Dev', 'Full Stack', 'Front-end', 'Back-end', 'Mobile', 'SaaS', 'E-commerce', 'App', 'Software'].some(
        term => d.toLowerCase().includes(term.toLowerCase())
      )
    );
    const isIntegrated = isDev && disciplines.some(d =>
      ['Design', 'Branding', 'UI/UX', 'Identidade Visual'].some(
        term => d.toLowerCase().includes(term.toLowerCase())
      )
    );

    let type: ProjectType = 'design';
    if (isIntegrated) type = 'integrated';
    else if (isDev) type = 'dev';

    return {
      ...project,
      project_type: type,
      tech_stack: project.tools || [],
      // Ensure client is normalized for the card (though card handles both, keeping consistent)
      client: typeof project.client === 'string' ? { name: project.client } : project.client,
      featured: project.featured || false,
    };
  }) || [];

  const allDisciplines = Array.from(
    new Set(
      allProjects.flatMap((project) =>
        project.disciplines?.map((d: any) => typeof d === 'string' ? d : d.discipline) || []
      )
    )
  );

  const filteredProjects = allProjects.filter((project) => {
    const matchesType = selectedType === 'all' || project.project_type === selectedType;
    const searchLower = searchTerm.toLowerCase();
    const clientName = typeof project.client === 'object' ? project.client.name : project.client;

    const matchesSearch =
      project.title.toLowerCase().includes(searchLower) ||
      (clientName || '').toLowerCase().includes(searchLower) ||
      project.summary.toLowerCase().includes(searchLower);

    const projectDisciplines = project.disciplines?.map((d: any) =>
      typeof d === 'string' ? d : d.discipline
    ) || [];
    const matchesDiscipline = !selectedDiscipline || projectDisciplines.includes(selectedDiscipline);

    return matchesType && matchesSearch && matchesDiscipline;
  });

  const featuredProjects = filteredProjects.filter((p: any) => p.featured);
  const regularProjects = filteredProjects.filter((p: any) => !p.featured);

  const counts = {
    all: allProjects.length,
    design: allProjects.filter(p => p.project_type === 'design').length,
    dev: allProjects.filter(p => p.project_type === 'dev').length,
    integrated: allProjects.filter(p => p.project_type === 'integrated').length,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 to-slate-900">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full mx-auto mb-4"
          />
          <p className="text-lg text-gray-400">Carregando projetos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 to-slate-900">
        <div className="text-center max-w-md px-4">
          <p className="text-lg text-gray-400 mb-6">
            Erro ao carregar projetos. Tente novamente mais tarde.
          </p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 relative">
      <ProceduralGroundBackground className="fixed inset-0 w-full h-full z-0 opacity-30" />

      <div className="relative z-10">

        <section className="relative py-32 md:py-40 lg:py-48 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/10 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-600/10 via-transparent to-transparent" />

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-5xl mx-auto"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-5 py-2.5 mb-8 text-sm font-medium bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-full backdrop-blur-sm"
              >
                <Sparkles className="w-4 h-4" />
                <span>Portfólio Premium</span>
              </motion.div>

              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 text-white">
                Criando o{" "}
                <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
                  Extraordinário
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                Uma coleção curada de trabalhos impactantes que transformam ideias em experiências digitais memoráveis.
              </p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap justify-center gap-4 mt-10"
              >
                <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-6 text-lg h-auto">
                  <Link to="/contato">
                    Iniciar Projeto
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="px-8 py-6 text-lg h-auto border-white/10 text-white hover:bg-white/5">
                  <Link to="/desenvolvimento">
                    <Zap className="mr-2 h-5 w-5" />
                    Stack Tecnológica
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="py-8 border-b border-white/5 bg-slate-950/30 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-3">
              {projectTypes.map((type) => {
                const Icon = type.icon;
                const isActive = selectedType === type.id;
                return (
                  <Button
                    key={type.id}
                    variant={isActive ? "default" : "outline"}
                    size="lg"
                    onClick={() => setSelectedType(type.id)}
                    className={`relative overflow-hidden ${isActive
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-0'
                      : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white'
                      }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {type.label}
                    <Badge
                      variant="secondary"
                      className={`ml-2 ${isActive ? 'bg-white/20 text-white' : 'bg-white/5'}`}
                    >
                      {counts[type.id]}
                    </Badge>
                  </Button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between mb-8"
            >
              <div className="relative flex-1 max-w-md w-full">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                  type="text"
                  placeholder="Buscar projetos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 bg-slate-900/50 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500/50"
                />
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="h-4 w-4 text-gray-500 shrink-0" />
                <span className="text-sm font-medium text-gray-400 shrink-0">Filtrar:</span>
                <div className="flex flex-wrap gap-2">
                  <Badge
                    variant={selectedDiscipline === '' ? 'default' : 'outline'}
                    className={`cursor-pointer transition-all ${selectedDiscipline === ''
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                      }`}
                    onClick={() => setSelectedDiscipline('')}
                  >
                    Todas
                  </Badge>
                  {allDisciplines.slice(0, 6).map((discipline) => (
                    <Badge
                      key={discipline}
                      variant={selectedDiscipline === discipline ? 'default' : 'outline'}
                      className={`cursor-pointer transition-all ${selectedDiscipline === discipline
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                        }`}
                      onClick={() => setSelectedDiscipline(discipline)}
                    >
                      {discipline}
                    </Badge>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex items-center justify-between text-sm text-gray-500 mb-8"
            >
              <p>
                Mostrando <span className="font-semibold text-white">{filteredProjects.length}</span> de{' '}
                <span className="font-semibold text-white">{allProjects.length}</span> projetos
              </p>
              {(searchTerm || selectedDiscipline || selectedType !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedDiscipline('');
                    setSelectedType('all');
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  Limpar filtros
                </Button>
              )}
            </motion.div>
          </div>
        </section>

        <section className="pb-20">
          <div className="container mx-auto px-4">
            <AnimatePresence mode="wait">
              {filteredProjects.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="text-center py-20"
                >
                  <div className="text-6xl mb-6">🔍</div>
                  <p className="text-xl text-gray-400 mb-6">
                    Nenhum projeto encontrado com os filtros aplicados.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedDiscipline('');
                      setSelectedType('all');
                    }}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    Limpar Filtros
                  </Button>
                </motion.div>
              ) : (
                <motion.div
                  key="grid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {featuredProjects.length > 0 && (
                    <div className="mb-16">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                      >
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 flex items-center gap-3">
                          <Sparkles className="w-8 h-8 text-yellow-400" />
                          Projetos em Destaque
                        </h2>
                        <p className="text-gray-400 text-lg">
                          Os trabalhos mais impactantes que desenvolvi
                        </p>
                      </motion.div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 justify-items-center max-w-7xl mx-auto w-full">
                        {featuredProjects.map((project: any, index: number) => {
                          const clientName = typeof project.client === 'object' ? project.client.name : project.client;
                          const coverImg = project.cover_image || project.coverImage || '';
                          const disciplines = project.disciplines?.map((d: any) => typeof d === 'string' ? d : d.discipline) || [];
                          const techStack = project.tech_stack || [];

                          return (
                            <ProjectCard
                              key={project.id}
                              title={project.title}
                              summary={project.summary}
                              coverImage={coverImg}
                              slug={project.slug}
                              year={project.year}
                              client={clientName}
                              disciplines={disciplines}
                              techStack={techStack}
                              projectType={project.project_type as 'design' | 'dev' | 'integrated'}
                              demoUrl={project.demoUrl}
                              githubUrl={project.githubUrl}
                              featured={true}
                              autoScroll={(project as any).auto_scroll || false}
                              index={index}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {regularProjects.length > 0 && (
                    <div>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                      >
                        <h2 className="text-2xl md:text-3xl font-bold text-white">
                          Todos os Projetos
                        </h2>
                      </motion.div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center max-w-7xl mx-auto w-full">
                        {regularProjects.map((project: any, index: number) => {
                          const clientName = typeof project.client === 'object' ? project.client.name : project.client;
                          const coverImg = project.cover_image || project.coverImage || '';
                          const disciplines = project.disciplines?.map((d: any) => typeof d === 'string' ? d : d.discipline) || [];
                          const techStack = project.tech_stack || [];

                          return (
                            <ProjectCard
                              key={project.id}
                              title={project.title}
                              summary={project.summary}
                              coverImage={coverImg}
                              slug={project.slug}
                              year={project.year}
                              client={clientName}
                              disciplines={disciplines}
                              techStack={techStack}
                              projectType={project.project_type as 'design' | 'dev' | 'integrated'}
                              demoUrl={project.demoUrl}
                              githubUrl={project.githubUrl}
                              featured={false}
                              autoScroll={(project as any).auto_scroll || false}
                              index={index + featuredProjects.length}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        <section className="py-24 relative overflow-hidden bg-gradient-to-b from-blue-900/10 to-transparent">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-blue-600/5 via-transparent to-transparent" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
                Pronto para seu próximo{" "}
                <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                  grande projeto?
                </span>
              </h2>
              <p className="text-xl text-gray-400 mb-10 leading-relaxed">
                Vamos trabalhar juntos para criar algo extraordinário que transforme sua marca
                e conecte com seu público de forma impactante.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-6 text-lg h-auto">
                  <Link to="/contato">
                    Solicitar Orçamento
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
}
